/**
 * TeamCardStatsCard - Side-by-side team card/foul statistics comparison
 *
 * Uses the same TeamRollupOut data as TeamsTab for consistency.
 */
import React from 'react'
import type { TeamRollupOut, TrendDirection } from '../types'
import { Sparkline } from './Sparkline'

interface TeamCardStatsCardProps {
  homeTeam: TeamRollupOut
  awayTeam: TeamRollupOut
  window: 'season' | 'last10' | 'last5' | 'last3'
}

const STAT_ROWS = [
  { key: 'avg_cards_y', label: 'Yellow Cards / 90', trendKey: true },
  { key: 'avg_cards_r', label: 'Red Cards / 90', trendKey: false },
  { key: 'avg_fouls', label: 'Fouls Committed / 90', trendKey: true },
  { key: 'avg_fouls_against', label: 'Fouls Drawn / 90', trendKey: false },
] as const

function computeTrend(windowVal: number, seasonVal: number, threshold = 0.1): TrendDirection {
  if (seasonVal === 0) return 'neutral'
  const diff = (windowVal - seasonVal) / seasonVal
  if (diff > threshold) return 'up'
  if (diff < -threshold) return 'down'
  return 'neutral'
}

function TrendArrow({ trend, show }: { trend: TrendDirection | null; show: boolean }) {
  if (!show || !trend) return null

  if (trend === 'up') {
    return <span style={{ color: 'var(--green)', marginLeft: 4 }}>▲</span>
  } else if (trend === 'down') {
    return <span style={{ color: 'var(--red)', marginLeft: 4 }}>▼</span>
  }
  return <span style={{ color: 'var(--muted)', marginLeft: 4 }}>—</span>
}

function formatStat(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return value.toFixed(1)
}

// Compute cards per match for sparkline (not available directly, so compute from totals)
function getCardsPerMatch(data: Record<string, any>): number {
  const matches = data.matches || 0
  if (matches === 0) return 0
  const yellows = data.cards_y || 0
  const reds = data.cards_r || 0
  return (yellows + reds) / matches
}

export function TeamCardStatsCard({
  homeTeam,
  awayTeam,
  window,
}: TeamCardStatsCardProps) {
  const homeData = homeTeam.windows[window] || {}
  const awayData = awayTeam.windows[window] || {}
  const homeSeasonData = homeTeam.windows.season || {}
  const awaySeasonData = awayTeam.windows.season || {}
  const homeMatches = homeData.matches || 0
  const awayMatches = awayData.matches || 0
  const showTrend = window !== 'season'

  if (!homeData.matches && !awayData.matches) {
    return (
      <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
        <div className="card-inner">
          <div className="small">No card statistics available</div>
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
      <div className="card-inner">
        {/* Team header row - matches TeamsTab exactly */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <span className="badge">
              {homeTeam.team.crest_url ? <img src={homeTeam.team.crest_url} alt="" /> : 'H'}
            </span>
            <div>
              <strong>{homeTeam.team.name}</strong>
              <div className="small">{homeMatches} matches</div>
            </div>
          </div>
          <div style={{ color: 'var(--muted)', fontWeight: 600 }}>vs</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
            <div style={{ textAlign: 'right' }}>
              <strong>{awayTeam.team.name}</strong>
              <div className="small">{awayMatches} matches</div>
            </div>
            <span className="badge">
              {awayTeam.team.crest_url ? <img src={awayTeam.team.crest_url} alt="" /> : 'A'}
            </span>
          </div>
        </div>

        {/* Stats comparison rows - using .live-stats and .stat-row */}
        <div className="live-stats">
          {STAT_ROWS.map((row) => {
            const homeValue = homeData[row.key] as number | undefined
            const awayValue = awayData[row.key] as number | undefined
            const homeSeasonValue = homeSeasonData[row.key] as number | undefined
            const awaySeasonValue = awaySeasonData[row.key] as number | undefined

            const homeTrend = row.trendKey && showTrend
              ? computeTrend(homeValue || 0, homeSeasonValue || 0)
              : null
            const awayTrend = row.trendKey && showTrend
              ? computeTrend(awayValue || 0, awaySeasonValue || 0)
              : null

            return (
              <div key={row.key} className="stat-row">
                <span className="stat-value" style={{ textAlign: 'left' }}>
                  {formatStat(homeValue)}
                  <TrendArrow trend={homeTrend} show={showTrend && row.trendKey} />
                </span>
                <span className="stat-label">{row.label}</span>
                <span className="stat-value" style={{ textAlign: 'right' }}>
                  <TrendArrow trend={awayTrend} show={showTrend && row.trendKey} />
                  {formatStat(awayValue)}
                </span>
              </div>
            )
          })}
        </div>

        {/* Totals row */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
            <span style={{ color: 'var(--muted)' }}>Yellows</span>
            <span style={{ fontWeight: 600, color: 'var(--yellow)' }}>{homeData.cards_y || 0}</span>
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
            <span style={{ color: 'var(--muted)' }}>Reds</span>
            <span style={{ fontWeight: 600, color: 'var(--red)' }}>{homeData.cards_r || 0}</span>
          </div>
          <div style={{ width: 1, background: 'var(--border)' }} />
          <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
            <span style={{ fontWeight: 600, color: 'var(--yellow)' }}>{awayData.cards_y || 0}</span>
            <span style={{ color: 'var(--muted)' }}>Yellows</span>
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
            <span style={{ fontWeight: 600, color: 'var(--red)' }}>{awayData.cards_r || 0}</span>
            <span style={{ color: 'var(--muted)' }}>Reds</span>
          </div>
        </div>
      </div>
    </div>
  )
}
